import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, MessageCircle, Flag, ShieldCheck, Truck, Star, MapPin, ArrowLeft, MoreHorizontal, ChevronLeft, ChevronRight, X, Share2, Link2, ChevronDown, Handshake } from "lucide-react";
import { PaymentMethodIcons, ShippingDeadlineNote } from "../components/PaymentMethods";
import { toast } from "sonner";
import { api, imgUrl, formatPrice, errText, CONDITIONS, getFees, withProtection, timeAgo } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ItemCard } from "../components/ItemCard";
import { OfferModal } from "../components/OfferModal";
import { BoostModal, boostRemaining } from "../components/BoostModal";
import { Rocket } from "lucide-react";
import { BadgeList } from "../components/BadgeChip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

const SHIP_LABELS = { STANDARD: "Standardversand (versichert)", DHL: "DHL Paket", HERMES: "Hermes", DPD: "DPD", GLS: "GLS", POST: "Deutsche Post", UPS: "UPS", ABHOLUNG: "Abholung" };

function FullscreenViewer({ images, index, setIndex, onClose, title }) {
  const [zoom, setZoom] = useState(false);
  useEffect(() => {
    const key = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIndex((i) => Math.min(images.length - 1, i + 1));
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(0, i - 1));
    };
    document.addEventListener("keydown", key);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", key); document.body.style.overflow = ""; };
  }, [images.length, onClose, setIndex]);
  const touch = useRef(null);
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col" data-testid="fullscreen-viewer" role="dialog" aria-label="Bildansicht">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="text-sm font-semibold" data-testid="viewer-counter">{index + 1} / {images.length}</span>
        <button data-testid="viewer-close" onClick={onClose} aria-label="Schließen" className="p-2 rounded-full hover:bg-white/10"><X className="w-6 h-6" /></button>
      </div>
      <div className="flex-1 flex items-center justify-center relative overflow-hidden"
        onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touch.current == null) return;
          const dx = e.changedTouches[0].clientX - touch.current;
          if (dx < -50) setIndex((i) => Math.min(images.length - 1, i + 1));
          if (dx > 50) setIndex((i) => Math.max(0, i - 1));
          touch.current = null;
        }}>
        {index > 0 && (
          <button data-testid="viewer-prev" onClick={() => setIndex(index - 1)} aria-label="Vorheriges Bild"
            className="absolute left-3 z-10 bg-white/15 hover:bg-white/25 text-white rounded-full p-2.5"><ChevronLeft className="w-6 h-6" /></button>
        )}
        <img src={imgUrl(images[index], "large")} alt={title}
          onClick={() => setZoom(!zoom)}
          className={`max-h-full max-w-full object-contain transition-transform duration-200 cursor-zoom-in ${zoom ? "scale-[1.8] cursor-zoom-out" : ""}`} />
        {index < images.length - 1 && (
          <button data-testid="viewer-next" onClick={() => setIndex(index + 1)} aria-label="Nächstes Bild"
            className="absolute right-3 z-10 bg-white/15 hover:bg-white/25 text-white rounded-full p-2.5"><ChevronRight className="w-6 h-6" /></button>
        )}
      </div>
    </div>
  );
}

export default function ItemDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [descOpen, setDescOpen] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("BETRUG");
  const [reportDetails, setReportDetails] = useState("");
  const [lawOpen, setLawOpen] = useState(false);
  const [fees, setFees] = useState(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    setItem(null); setActiveImg(0); setDescOpen(false);
    api.get(`/items/${id}`).then(({ data }) => setItem(data)).catch(() => setItem(false));
    getFees().then(setFees);
    window.scrollTo(0, 0);
  }, [id]);

  if (item === null) return <div className="max-w-6xl mx-auto px-4 py-10"><div className="h-96 bg-muted rounded-2xl animate-pulse" /></div>;
  if (item === false) return <div className="max-w-6xl mx-auto px-4 py-20 text-center text-muted-foreground">Artikel nicht gefunden.</div>;

  const isOwner = user && user.id === item.seller_id;
  const changeStatus = (status) => async () => {
    try {
      await api.patch(`/items/${item.id}/status`, { status });
      setItem({ ...item, status });
      toast.success(status === "HIDDEN" ? "Artikel pausiert" : "Artikel ist wieder online");
    } catch (e) { toast.error(errText(e)); }
  };
  const deleteItem = async () => {
    if (!window.confirm("Diesen Artikel endgültig löschen?")) return;
    try {
      await api.patch(`/items/${item.id}/status`, { status: "DELETED" });
      toast.success("Artikel gelöscht");
      nav("/verkaeufer");
    } catch (e) { toast.error(errText(e)); }
  };
  const images = item.image_ids || [];
  const requireAuth = (fn) => () => { if (!user) return nav("/anmelden"); fn(); };
  const protectedPrice = withProtection(item.price, fees);
  const canBuy = (item.status === "ACTIVE" || (item.status === "RESERVED" && item.my_offer?.status === "ACCEPTED")) && item.seller_payout_ready !== false;
  const payoutNotReady = item.seller_payout_ready === false;
  const buyPrice = item.my_offer?.status === "ACCEPTED" ? item.my_offer.offer_price : item.price;
  const descLong = (item.description || "").length > 320;

  const toggleFav = requireAuth(async () => {
    try {
      const { data } = await api.post(`/favorites/${item.id}`);
      setItem({ ...item, is_favorite: data.favorite, favorites_count: Math.max(0, (item.favorites_count || 0) + (data.favorite ? 1 : -1)) });
    } catch {}
  });

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: item.title, url }); } catch {} }
    else { await navigator.clipboard.writeText(url); toast.success("Link kopiert"); }
  };
  const copyLink = async () => { await navigator.clipboard.writeText(window.location.href); toast.success("Link kopiert"); };

  const sendMessage = async () => {
    try {
      await api.post("/conversations", { item_id: item.id, body: msgText });
      toast.success("Nachricht gesendet");
      setMsgOpen(false);
      nav("/nachrichten");
    } catch (e) { toast.error(errText(e)); }
  };

  const report = async () => {
    try {
      const { data } = await api.post("/reports", { entity_type: "item", entity_id: item.id, reason: reportReason, details: reportDetails });
      toast.success(`Meldung ${data.number || ""} wurde übermittelt`.trim());
      setReportOpen(false);
    } catch (e) { toast.error(errText(e)); }
  };

  const onGalleryScroll = () => {
    const el = galleryRef.current;
    if (el) setActiveImg(Math.round(el.scrollLeft / el.clientWidth));
  };

  const details = [
    item.brand && ["Marke", <Link to={`/suche?brand=${encodeURIComponent(item.brand)}`} className="text-accent font-semibold hover:underline">{item.brand}</Link>],
    ["Zustand", CONDITIONS[item.condition] || item.condition],
    item.color && ["Farbe", item.color],
    item.size && ["Größe", item.size],
    item.weight && ["Gewicht", `${item.weight} ${item.weight_unit || "g"}`],
    item.material && ["Material", item.material],
    item.breadcrumb?.length > 0 && ["Kategorie", item.breadcrumb.map((b) => b.name).join(" / ")],
    item.location && ["Standort", item.location],
    ["Hochgeladen", timeAgo(item.created_at)],
  ].filter(Boolean);

  const buyBtn = (extra = "") => (
    <button data-testid="buy-now-btn" onClick={() => nav(`/kaufen/${item.id}`)}
      className={`bg-accent text-white font-bold rounded-xl active:scale-[0.98] transition-transform hover:opacity-90 ${extra}`}>
      {item.my_offer?.status === "ACCEPTED" ? `Kaufen · ${formatPrice(buyPrice)}` : "Kaufen"}
    </button>
  );

  const negotiateAllowed = item.negotiable !== false && !item.my_offer && !isOwner && item.status === "ACTIVE";
  const negotiateBtn = (extra = "") => negotiateAllowed ? (
    user ? (
      <OfferModal item={item} onSent={() => api.get(`/items/${item.id}`).then(({ data }) => setItem(data))}
        trigger={
          <button data-testid="negotiate-btn" className={`bg-white border-2 border-accent text-accent font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-accent/5 transition-colors ${extra}`}>
            <Handshake className="w-4 h-4" /> Verhandeln
          </button>
        } />
    ) : (
      <button data-testid="negotiate-login-btn" onClick={() => nav("/anmelden")}
        className={`bg-white border-2 border-accent text-accent font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-accent/5 transition-colors ${extra}`}>
        <Handshake className="w-4 h-4" /> Verhandeln
      </button>
    )
  ) : null;

  const moreMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="item-more-btn" aria-label="Mehr Optionen" className="bg-white/90 backdrop-blur shadow-sm rounded-full p-2.5 hover:bg-white transition-colors">
          <MoreHorizontal className="w-5 h-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem data-testid="menu-share" onClick={share}><Share2 className="w-4 h-4 mr-2" /> Artikel teilen</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-copy-link" onClick={copyLink}><Link2 className="w-4 h-4 mr-2" /> Link kopieren</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-save" onClick={toggleFav}><Heart className="w-4 h-4 mr-2" /> {item.is_favorite ? "Aus Favoriten entfernen" : "Artikel speichern"}</DropdownMenuItem>
        {user && !isOwner && <DropdownMenuItem data-testid="menu-report" onClick={() => setReportOpen(true)} className="text-destructive"><Flag className="w-4 h-4 mr-2" /> Artikel melden</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const favPill = (
    <button data-testid="item-fav-btn" onClick={toggleFav} aria-label="Favorit"
      className="bg-white/95 shadow-sm rounded-full px-3 py-2 flex items-center gap-1.5 hover:scale-105 active:scale-95 transition-transform">
      <Heart className={`w-5 h-5 ${item.is_favorite ? "fill-accent text-accent" : "text-foreground"}`} />
      <span className="text-sm font-semibold" data-testid="fav-count">{item.favorites_count || 0}</span>
    </button>
  );

  return (
    <div className="pb-28 md:pb-0">
      {viewerOpen && <FullscreenViewer images={images} index={activeImg} setIndex={setActiveImg} onClose={() => setViewerOpen(false)} title={item.title} />}

      {/* ===== Mobile Gallery (edge-to-edge) ===== */}
      <div className="md:hidden relative" data-testid="mobile-gallery">
        <div ref={galleryRef} onScroll={onGalleryScroll}
          className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar">
          {images.length > 0 ? images.map((img, i) => (
            <img key={img} src={imgUrl(img, "large")} alt={`${item.title} Bild ${i + 1}`}
              loading={i === 0 ? "eager" : "lazy"}
              onClick={() => { setActiveImg(i); setViewerOpen(true); }}
              className="w-full shrink-0 snap-center aspect-square object-cover bg-white" />
          )) : <div className="w-full aspect-square bg-muted flex items-center justify-center text-muted-foreground text-sm">Kein Bild</div>}
        </div>
        <button onClick={() => nav(-1)} data-testid="mobile-back-btn" aria-label="Zurück"
          className="absolute top-3 left-3 bg-white/90 backdrop-blur shadow-sm rounded-full p-2.5 hover:bg-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="absolute top-3 right-3">{moreMenu}</div>
        <div className="absolute bottom-3 right-3">{favPill}</div>
        {images.length > 1 && (
          <span className="absolute bottom-3 left-3 bg-foreground/70 text-white text-xs font-semibold px-2.5 py-1 rounded-full" data-testid="mobile-image-counter">
            {activeImg + 1} / {images.length}
          </span>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:py-8">
        {/* Breadcrumbs */}
        {item.breadcrumb?.length > 0 && (
          <nav aria-label="Breadcrumb" className="hidden md:flex items-center gap-1.5 text-sm text-muted-foreground mb-4" data-testid="breadcrumbs">
            <Link to="/" className="hover:text-accent">Start</Link>
            {item.breadcrumb.map((b) => (
              <span key={b.id} className="flex items-center gap-1.5">
                <span>/</span>
                <Link to={b.type === "category" ? `/suche?category_id=${b.id}` : `/suche?category_id=${b.parent_id}&subcategory_id=${b.id}`} className="hover:text-accent">{b.name}</Link>
              </span>
            ))}
          </nav>
        )}

        <div className="md:grid md:grid-cols-[1fr_400px] lg:grid-cols-[1fr_430px] md:gap-8 md:items-start">
          {/* ===== Desktop Gallery ===== */}
          <div className="hidden md:block">
            <div className="relative rounded-2xl overflow-hidden border border-border bg-white">
              {images.length > 0 ? (
                <img data-testid="item-main-image" src={imgUrl(images[activeImg], "large")} alt={item.title}
                  onClick={() => setViewerOpen(true)}
                  className="w-full aspect-[4/3] object-contain bg-white cursor-zoom-in" />
              ) : <div className="w-full aspect-[4/3] bg-muted flex items-center justify-center text-muted-foreground">Kein Bild</div>}
              {images.length > 1 && (
                <>
                  {activeImg > 0 && <button data-testid="gallery-prev" onClick={() => setActiveImg(activeImg - 1)} aria-label="Vorheriges Bild"
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 shadow-sm rounded-full p-2 hover:bg-white"><ChevronLeft className="w-5 h-5" /></button>}
                  {activeImg < images.length - 1 && <button data-testid="gallery-next" onClick={() => setActiveImg(activeImg + 1)} aria-label="Nächstes Bild"
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 shadow-sm rounded-full p-2 hover:bg-white"><ChevronRight className="w-5 h-5" /></button>}
                  <span className="absolute bottom-3 left-3 bg-foreground/70 text-white text-xs font-semibold px-2.5 py-1 rounded-full">{activeImg + 1} / {images.length}</span>
                </>
              )}
              <div className="absolute bottom-3 right-3">{favPill}</div>
              <div className="absolute top-3 right-3">{moreMenu}</div>
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto">
                {images.map((img, i) => (
                  <button key={img} data-testid={`item-thumb-${i}`} onClick={() => setActiveImg(i)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 shrink-0 ${i === activeImg ? "border-accent" : "border-transparent"}`}>
                    <img src={imgUrl(img, "thumb")} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ===== Info / Purchase box ===== */}
          <div className="md:sticky md:top-24 pt-4 md:pt-0">
            <h1 className="text-[24px] md:text-3xl font-bold leading-snug" data-testid="item-title">{item.title}</h1>
            <p className="text-sm text-foreground/70 mt-1.5" data-testid="item-condition">
              {CONDITIONS[item.condition] || item.condition}
              {item.brand && <> · <Link to={`/suche?brand=${encodeURIComponent(item.brand)}`} className="text-accent font-semibold hover:underline" data-testid="item-brand-link">{item.brand}</Link></>}
            </p>
            <p className="text-[26px] font-bold mt-3" data-testid="item-price">{formatPrice(item.price)}</p>
            {protectedPrice != null && (
              <p className="text-sm text-accent font-semibold flex items-center gap-1.5 mt-0.5" data-testid="item-protected-price">
                <ShieldCheck className="w-4 h-4" /> {formatPrice(protectedPrice)} inkl. KLEDIO-Käuferschutz
              </p>
            )}

            {item.authenticity_status === "AUTHENTICITY_VERIFIED" && (
              <p className="text-sm font-bold text-accent mt-2" data-testid="authenticated-badge">KLEDIO geprüft ✓ — Echtheit bestätigt</p>
            )}

            {/* CTAs desktop */}
            {!isOwner && canBuy && (
              <div className="hidden md:block mt-5 space-y-2.5">
                {item.my_offer?.status === "ACCEPTED" && (
                  <div className="bg-accent/10 border border-accent/30 rounded-xl p-3 text-sm font-semibold text-accent" data-testid="offer-accepted-banner">
                    Angebot angenommen: {formatPrice(item.my_offer.offer_price)} — reserviert für dich!
                  </div>
                )}
                {item.my_offer?.status === "PENDING" && (
                  <div className="bg-secondary rounded-xl p-3 text-sm" data-testid="offer-pending-banner">
                    Dein Angebot über <span className="font-bold">{formatPrice(item.my_offer.offer_price)}</span> wartet auf Antwort.
                  </div>
                )}
                {buyBtn("w-full py-4")}
                {negotiateBtn("w-full py-4")}
                <button data-testid="message-seller-btn" onClick={requireAuth(() => setMsgOpen(true))}
                  className="w-full border border-border text-foreground font-bold py-4 rounded-xl hover:bg-secondary transition-colors flex items-center justify-center gap-2">
                  <MessageCircle className="w-4 h-4" /> Frage stellen
                </button>
                <ShippingDeadlineNote className="pt-1" />
                <PaymentMethodIcons label="Zahlung:" />
              </div>
            )}
            {!isOwner && !canBuy && (
              <div className="mt-5 bg-muted rounded-xl p-4 text-sm font-semibold" data-testid="item-unavailable-notice">
                {item.status === "SOLD" ? "Dieser Artikel wurde bereits verkauft." : item.status === "RESERVED" ? "Dieser Artikel ist gerade reserviert." : payoutNotReady ? "Dieser Artikel kann derzeit nicht gekauft werden." : "Dieser Artikel ist nicht verfügbar."}
              </div>
            )}
            {isOwner && payoutNotReady && item.status === "ACTIVE" && (
              <div className="mt-5 border border-amber-300 bg-amber-50 rounded-xl p-4" data-testid="owner-payout-warning">
                <p className="text-sm font-bold text-amber-800">Auszahlungen noch nicht eingerichtet</p>
                <p className="text-xs text-amber-700 mt-1">Käufer können diesen Artikel erst kaufen, wenn du deine Auszahlung über Stripe eingerichtet hast.</p>
                <button data-testid="setup-payouts-btn" onClick={() => nav("/verkaeufer")}
                  className="mt-3 bg-foreground text-white text-xs font-bold px-4 py-2.5 rounded-full hover:opacity-90">Stripe Connect einrichten</button>
              </div>
            )}
            {isOwner && ["ACTIVE", "HIDDEN", "DRAFT"].includes(item.status) && (
              <div className="mt-5 space-y-2.5" data-testid="owner-actions">
                {item.status === "ACTIVE" && (
                  <button data-testid="edit-item-btn" onClick={() => nav(`/verkaufen?edit=${item.id}`)}
                    className="w-full border border-border font-bold py-3.5 rounded-xl hover:bg-secondary transition-colors">Artikel bearbeiten</button>
                )}
                {item.status === "ACTIVE" && (item.my_boost ? (
                  <div className="border border-accent/30 bg-accent/5 rounded-xl p-3.5 flex items-center justify-between gap-2 flex-wrap" data-testid="my-boost-status">
                    <span className="text-sm font-bold text-accent flex items-center gap-1.5"><Rocket className="w-4 h-4" /> Boost aktiv · noch {boostRemaining(item.my_boost.end_at)}</span>
                    <BoostModal itemId={item.id} extend
                      trigger={<button data-testid="boost-extend-btn" className="text-xs font-bold border border-accent text-accent rounded-full px-3.5 py-1.5 hover:bg-accent/10">Verlängern</button>} />
                  </div>
                ) : (
                  <BoostModal itemId={item.id}
                    trigger={<button data-testid="boost-item-btn" className="w-full bg-foreground text-white font-bold py-3.5 rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2"><Rocket className="w-4 h-4" /> Artikel boosten</button>} />
                ))}
                <div className="flex gap-2.5">
                  <button data-testid="toggle-item-status-btn" onClick={changeStatus(item.status === "ACTIVE" ? "HIDDEN" : "ACTIVE")}
                    className="flex-1 border border-border font-bold py-3 rounded-xl hover:bg-secondary transition-colors text-sm">
                    {item.status === "ACTIVE" ? "Pausieren" : "Wieder aktivieren"}
                  </button>
                  <button data-testid="delete-item-btn" onClick={deleteItem}
                    className="flex-1 border border-red-300 text-red-600 font-bold py-3 rounded-xl hover:bg-red-50 transition-colors text-sm">
                    Artikel löschen
                  </button>
                </div>
                {item.status === "HIDDEN" && <p className="text-xs text-muted-foreground">Dieser Artikel ist pausiert und für Käufer nicht sichtbar.</p>}
              </div>
            )}
            {/* Frage stellen mobile (inline) */}
            {!isOwner && canBuy && (
              <button data-testid="message-seller-btn-mobile" onClick={requireAuth(() => setMsgOpen(true))}
                className="md:hidden mt-4 w-full border border-border text-foreground font-bold py-3.5 rounded-xl hover:bg-secondary transition-colors flex items-center justify-center gap-2">
                <MessageCircle className="w-4 h-4" /> Frage stellen
              </button>
            )}

            {/* Beschreibung */}
            <div className="mt-6 border-t border-border pt-5">
              <h2 className="text-base font-bold mb-2">Beschreibung</h2>
              <p className={`text-[15px] md:text-sm whitespace-pre-wrap text-foreground/80 ${descLong && !descOpen ? "line-clamp-5" : ""}`} data-testid="item-description">{item.description}</p>
              {descLong && (
                <button data-testid="desc-toggle" onClick={() => setDescOpen(!descOpen)} className="text-sm font-semibold text-accent mt-1.5 hover:underline">
                  {descOpen ? "Weniger anzeigen" : "Mehr anzeigen"}
                </button>
              )}
            </div>

            {/* Details */}
            <div className="mt-6 border-t border-border pt-5" data-testid="item-details-table">
              <h2 className="text-base font-bold mb-3">Artikelinformationen</h2>
              <dl className="space-y-2">
                {details.map(([k, v]) => (
                  <div key={k} className="flex text-sm">
                    <dt className="w-32 shrink-0 text-muted-foreground">{k}</dt>
                    <dd className="font-medium">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Versand */}
            <div className="mt-6 border-t border-border pt-5" data-testid="item-shipping-block">
              <button onClick={() => setShipOpen(!shipOpen)} data-testid="shipping-toggle" className="w-full flex items-center justify-between">
                <span className="text-base font-bold flex items-center gap-2"><Truck className="w-4 h-4 text-accent" /> Versand</span>
                <span className="text-sm font-semibold flex items-center gap-1">
                  {item.shipping_price > 0 ? `ab ${formatPrice(item.shipping_price)}` : "Kostenlos"}
                  <ChevronDown className={`w-4 h-4 transition-transform ${shipOpen ? "rotate-180" : ""}`} />
                </span>
              </button>
              {shipOpen && (
                <div className="mt-3 space-y-1.5" data-testid="shipping-options-list">
                  {(item.shipping_methods || ["STANDARD"]).map((m) => {
                    const p = m === "ABHOLUNG" || item.free_shipping ? 0 : (item.shipping_prices?.[m] ?? item.shipping_price);
                    return (
                      <div key={m} className="flex justify-between text-sm border border-border rounded-xl px-3.5 py-2.5">
                        <span>{SHIP_LABELS[m] || m}</span>
                        <span className="font-semibold">{p > 0 ? formatPrice(p) : "Kostenlos"}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Trust */}
            <div className="mt-6 border border-border bg-white rounded-2xl p-4" data-testid="trust-block">
              <p className="font-bold text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-accent" /> Sicher kaufen und verkaufen</p>
              <p className="text-sm text-muted-foreground mt-1.5">Dein Kauf wird durch sichere Zahlungsabwicklung, KLEDIO Käuferschutz und unseren Support abgesichert.</p>
              <Link to="/kaeuferschutz" data-testid="buyer-protection-link" className="text-sm font-semibold text-accent hover:underline mt-1.5 inline-block">Wie der Käuferschutz funktioniert</Link>
              <div className="border-t border-border mt-3 pt-3 space-y-1.5 text-sm">
                <p className="flex items-center gap-2"><Truck className="w-4 h-4 text-primary" /> Versand mit Sendungsverfolgung</p>
                {item.location && <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> {item.location}</p>}
                {item.brand_high_risk && item.authenticity_status !== "AUTHENTICITY_VERIFIED" && (
                  <p className="text-xs text-muted-foreground" data-testid="high-risk-notice">Dieser Artikel kann einer Echtheitsprüfung unterliegen.</p>
                )}
              </div>
            </div>

            {/* Seller Card */}
            {item.seller && (
              <div className="mt-4 border border-border rounded-2xl p-4" data-testid="seller-card">
                <Link to={`/profil/${item.seller.id}`} data-testid="seller-profile-link" className="flex items-center gap-3 hover:opacity-80">
                  <div className="w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                    {item.seller.username?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm flex items-center gap-2 flex-wrap">@{item.seller.username}
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-secondary" data-testid="seller-type-badge">{item.seller.is_business ? "Gewerblicher Verkäufer" : "Privat"}</span>
                    </p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      {item.seller.rating_avg ? <><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {item.seller.rating_avg} ({item.seller.rating_count} Bewertungen)</> : "Noch keine Bewertungen"}
                      {" · "}{item.seller.sales_count} Verkäufe
                    </p>
                  </div>
                </Link>
                {item.seller.badges?.length > 0 && <div className="mt-2.5"><BadgeList badges={item.seller.badges} max={3} /></div>}
                {(() => {
                  const infos = [];
                  if (item.seller.active_items >= 5) infos.push("Häufig aktiv – sorgt regelmäßig für neue Artikel");
                  if (item.seller.avg_ship_hours != null && item.seller.avg_ship_hours <= 24) infos.push("Schneller Versand – versendet normalerweise innerhalb von 24 Stunden");
                  if (item.seller.avg_response_minutes != null) infos.push(`Antwortet normalerweise innerhalb von ${item.seller.avg_response_minutes < 5 ? "wenigen Minuten" : item.seller.avg_response_minutes < 60 ? `${item.seller.avg_response_minutes} Min.` : `${Math.max(1, Math.round(item.seller.avg_response_minutes / 60))} Std.`}`);
                  if (item.seller.response_rate != null) infos.push(`${Math.round(item.seller.response_rate * 100)} % Antwortquote`);
                  return infos.length > 0 && (
                    <div className="mt-3 border-t border-border pt-3 space-y-1" data-testid="seller-infos">
                      {infos.map((t) => <p key={t} className="text-xs text-muted-foreground flex items-center gap-1.5"><span className="text-accent">✓</span> {t}</p>)}
                    </div>
                  );
                })()}
                {item.seller.last_seen_text && <p className="text-xs text-muted-foreground mt-2" data-testid="seller-last-seen">{item.seller.last_seen_text}</p>}
                {!isOwner && (
                  <div className="flex gap-2 mt-3">
                    <button data-testid="follow-seller-btn" onClick={requireAuth(async () => {
                      try {
                        const { data } = await api.post(`/users/${item.seller.id}/follow`);
                        setItem({ ...item, is_following: data.following });
                        toast.success(data.following ? "Du folgst jetzt @" + item.seller.username : "Nicht mehr gefolgt");
                      } catch (e) { toast.error(errText(e)); }
                    })}
                      className={`flex-1 text-sm font-bold py-2.5 rounded-full transition-colors ${item.is_following ? "bg-secondary text-foreground" : "border-2 border-foreground hover:border-accent hover:text-accent"}`}>
                      {item.is_following ? "Folge ich ✓" : "Folgen"}
                    </button>
                    {user && (
                      <button data-testid="report-seller-btn" onClick={async () => {
                        try {
                          const { data } = await api.post("/reports", { entity_type: "user", entity_id: item.seller.id, reason: "SONSTIGES", details: `Verkäufer gemeldet (Artikel ${item.id})` });
                          toast.success(`Verkäufer gemeldet (${data.number})`, { description: "Unser Team prüft die Meldung." });
                        } catch (e) { toast.error(errText(e)); }
                      }} className="text-xs text-muted-foreground px-3 hover:text-destructive">Verkäufer melden</button>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 border border-border rounded-2xl p-4 bg-secondary/30 text-xs text-muted-foreground" data-testid="buyer-legal-info">
              <p className="font-bold text-foreground mb-1">Informationen für Käufer</p>
              <p>Wenn du einen Artikel über KLEDIO kaufst, gelten die KLEDIO-Käuferschutzbedingungen. Je nach Verkäuferstatus können unterschiedliche gesetzliche Rechte gelten.</p>
              <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                <Link to="/kaeuferschutz" className="text-accent hover:underline">Käuferschutz</Link>
                <Link to="/agb" className="text-accent hover:underline">AGB</Link>
                <Link to="/rueckerstattungen" className="text-accent hover:underline">Rückerstattung</Link>
                <button type="button" onClick={() => setLawOpen(!lawOpen)} data-testid="law-notice-toggle"
                  className="text-accent hover:underline font-normal">
                  Verbraucherrechte
                </button>
              </p>
              {lawOpen && (
                <p className="mt-3 pt-3 border-t border-border/60 text-[11px] text-muted-foreground/80 leading-snug" data-testid="consumer-law-notice">
                  Für deine Bestellungen bei anderen Verbrauchern gelten die Verbraucherschutzgesetze nicht.
                  Insbesondere finden das Widerrufsrecht gemäß{" "}
                  <a href="https://www.gesetze-im-internet.de/bgb/__355.html" target="_blank" rel="noopener noreferrer"
                    className="underline underline-offset-2 hover:text-accent" data-testid="bgb-355-link">
                    §§ 355 ff. des deutschen Bürgerlichen Gesetzbuchs (BGB)
                  </a>{" "}
                  sowie die zweijährige gesetzliche Gewährleistung, die nach EU-Recht harmonisiert ist, keine
                  Anwendung. Die gesetzlichen Gewährleistungsrechte einschließlich der Haftung für Sachmängel gemäß{" "}
                  <a href="https://www.gesetze-im-internet.de/bgb/__433.html" target="_blank" rel="noopener noreferrer"
                    className="underline underline-offset-2 hover:text-accent">
                    §§ 433 ff. des deutschen Bürgerlichen Gesetzbuchs (BGB)
                  </a>{" "}
                  finden Anwendung (sofern der Verkäufer sie nicht aufgehoben hat). Die Rechte der Käufer im Fall
                  eines mangelhaften Artikels sind in{" "}
                  <a href="https://www.gesetze-im-internet.de/bgb/__437.html" target="_blank" rel="noopener noreferrer"
                    className="underline underline-offset-2 hover:text-accent">
                    § 437 des deutschen Bürgerlichen Gesetzbuchs (BGB)
                  </a>{" "}
                  geregelt. Jeder Kauf, der über den „Kaufen"-Button getätigt wird, unterliegt unseren{" "}
                  <Link to="/rueckerstattungen" className="underline underline-offset-2 hover:text-accent">Rückerstattungsrichtlinien</Link>.
                </p>
              )}
            </div>

            {isOwner && item.authenticity_status === "AUTHENTICITY_PENDING" && (
              <div className="mt-4 border border-accent/40 bg-accent/5 rounded-2xl p-4" data-testid="authenticity-upload-box">
                <p className="text-sm font-bold">Echtheitsnachweis erforderlich</p>
                <p className="text-xs text-muted-foreground mt-1">Lade Kaufbeleg, Seriennummer, Zertifikat oder Detailfotos hoch. Nur unser Prüfteam kann diese Dateien sehen.</p>
                <label className="inline-block mt-2 text-xs font-bold border border-border rounded-full px-4 py-2 cursor-pointer hover:bg-white">
                  Nachweise hochladen
                  <input type="file" data-testid="authenticity-upload-input" accept="image/jpeg,image/png,image/webp" multiple className="hidden"
                    onChange={async (e) => {
                      try {
                        const ids = [];
                        for (const f of e.target.files) {
                          const fd = new FormData(); fd.append("file", f);
                          const { data } = await api.post("/uploads/image", fd);
                          ids.push(data.image_id);
                        }
                        await api.post(`/items/${item.id}/authenticity-docs`, { image_ids: ids });
                        toast.success("Nachweise übermittelt", { description: "Unser Prüfteam meldet sich in Kürze." });
                      } catch (err) { toast.error(errText(err)); }
                    }} />
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Weitere Artikel des Verkäufers */}
        {item.seller_items?.length > 0 && (
          <div className="mt-12" data-testid="seller-items-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Weitere Artikel von @{item.seller?.username}</h2>
              <Link to={`/profil/${item.seller?.id}`} className="text-sm font-semibold text-accent hover:underline">Alle ansehen</Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
              {item.seller_items.map((it, i) => <ItemCard key={it.id} item={it} index={i} />)}
            </div>
          </div>
        )}

        {/* Ähnliche Artikel */}
        {item.similar?.length > 0 && (
          <div className="mt-12" data-testid="similar-items-section">
            <h2 className="text-lg font-bold mb-4">Das könnte dir gefallen</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
              {item.similar.map((it, i) => <ItemCard key={it.id} item={it} index={i} />)}
            </div>
          </div>
        )}
      </div>

      {/* ===== Mobile Fixed Action Bar ===== */}
      {!isOwner && canBuy && (
        <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-border shadow-[0_-4px_20px_rgba(0,0,0,0.06)] px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex gap-3" data-testid="mobile-action-bar">
          {negotiateAllowed ? negotiateBtn("flex-1 py-3.5") : <div className="flex-1" />}
          {buyBtn("flex-1 py-3.5")}
        </div>
      )}

      {/* Frage-Dialog */}
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Frage zu: {item.title}</DialogTitle></DialogHeader>
          <textarea data-testid="message-textarea" className="w-full rounded-xl border border-border p-3 text-sm min-h-28 focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="Ist der Artikel noch verfügbar?" value={msgText} onChange={(e) => setMsgText(e.target.value)} />
          <button data-testid="message-send-btn" onClick={sendMessage} disabled={!msgText.trim()}
            className="bg-primary text-primary-foreground font-bold py-3 rounded-full hover:opacity-90 disabled:opacity-50">Senden</button>
        </DialogContent>
      </Dialog>

      {/* Melden-Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Artikel melden</DialogTitle></DialogHeader>
          <Select value={reportReason} onValueChange={setReportReason}>
            <SelectTrigger data-testid="report-reason-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="BETRUG">Betrug</SelectItem>
              <SelectItem value="FAELSCHUNG">Fälschung</SelectItem>
              <SelectItem value="MARKENRECHT">Markenrechtsverletzung</SelectItem>
              <SelectItem value="VERBOTENER_ARTIKEL">Verbotener Artikel</SelectItem>
              <SelectItem value="FALSCHE_BESCHREIBUNG">Falsche Beschreibung</SelectItem>
              <SelectItem value="UNANGEMESSEN">Unangemessener Inhalt</SelectItem>
              <SelectItem value="SPAM">Spam</SelectItem>
              <SelectItem value="SONSTIGES">Sonstiges</SelectItem>
            </SelectContent>
          </Select>
          <textarea data-testid="report-details-input" className="w-full rounded-xl border border-border p-3 text-sm min-h-20" placeholder="Details (optional)" value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} />
          <button data-testid="report-submit-btn" onClick={report} className="bg-destructive text-white font-bold py-3 rounded-full hover:opacity-90">Melden</button>
        </DialogContent>
      </Dialog>

      {/* Desktop: Artikel melden Link */}
      {user && !isOwner && (
        <div className="hidden md:block max-w-6xl mx-auto px-6 pb-10">
          <button data-testid="report-item-btn" onClick={() => setReportOpen(true)} className="text-xs text-muted-foreground flex items-center gap-1.5 hover:text-destructive transition-colors">
            <Flag className="w-3.5 h-3.5" /> Artikel melden
          </button>
        </div>
      )}
    </div>
  );
}
